import type { TranslationResources } from '../../types';

export const zhHans = {
  common: {
    labOpening: '正在进入当前 Workspace 的 Mylo lab…',
    labLaunchError:
      '无法打开 Mylo lab。请检查连接并重试；若当前区域尚未开通，请联系支持。',
    labRetry: '重试',

    brand: 'Mylo Studio',
    continue: '继续',
    cancel: '取消',
    save: '保存更改',
    reset: '重置演示数据',
    skip: '跳转到内容',
    search: '搜索',
    new: '新建',
    beta: '测试版',
    open: '打开',
    back: '返回',
    language: '语言',
    english: '英语',
    russian: '俄语',
    chinese: '简体中文',
    credits: '肥皂币',
    sandbox: '沙盒 · 国际区域',
    noResults: '这里暂时没有内容',
    viewAll: '查看全部',
    comingSoon: '即将推出',
    errorStart: 'Mylo Studio 无法启动。',
    reload: '重新加载',
    switchToLightTheme: '切换到浅色主题',
    switchToDarkTheme: '切换到深色主题',
    download: '下载',
    signOut: '退出登录',
    enterStudio: '进入 Mylo Studio',
    demoHint: 'OAuth 配置完成前，可先使用邮箱验证码登录。',
    metricCreative: '创意变体',
    metricMarket: '市场背景',
    metricAgent: 'Agent 工作状态',
    paidBeta: '付费测试版 · 生产环境',
    signIn: '登录',
    startCreating: '开始创作',
  },
  home: {
    skip: '跳转到内容',
    homeLabel: 'Mylo Studio 首页',
    sectionsLabel: '首页分区',
    galleryLabel: '使用 Agent 创作的视频案例',
    nav: ['创作能力', '案例', '使用方式', '模型'],
    actions: ['打开 Agent', '创作视频', '查看案例'],
    hero: {
      badge: 'AI 视频 Agent',
      title: '将任何想法变成视频。',
      titleLines: ['将', '任何想法', '变成视频。'],
      description:
        '用俄语表达，添加照片、视频，或只提供一个想法。Mylo Agent 会理解任务、选择模型，并帮你把它变成完整视频。',
      highlights: [
        '直接说俄语',
        '不必学习提示词语言，只需说清你想要什么。',
        '一个 Agent，汇集优秀模型',
        '技术不断变化，你不必一直追赶。',
      ],
      prompt: [
        '提示词',
        '把它做成一支来自未来的霓虹音乐视频。',
        'Agent 已理解',
        'Agent 正在创作',
        '实时预览',
        '视频',
      ],
    },
    overview: [
      '你的 AI 视频工作室',
      '一个想法，一个 Agent，无限种视频。',
      '表情包、剪辑、故事、音乐视频、角色，甚至一个全新的世界——把想法告诉 Agent，它会通过脚本、场景和视频帮你把想法变成现实。',
    ],
    formats: ['想法', '参考', 'Agent', '视频'],
    showcase: [
      '你可以创作什么',
      '滑动、观看，创作你的版本。',
      '一个想法可以变成几十种不同的视频。改变风格、角色、氛围和方向，Agent 会帮你做出新版本。',
      '想法 / Agent / 视频',
    ],
    materials: ['使用方式', '从任何内容开始。', '想法 / 照片 / 视频'],
    pipeline: [
      ['想法', '文字 + 照片 + 视频'],
      ['理解', '风格 + 角色 + 场景'],
      ['Agent', '模型 + 生成'],
      ['视频', '完整结果'],
    ],
    submitItems: [
      '写下想法，或上传照片、视频与参考素材。',
      'Agent 会理解你想要的风格、角色、情绪和结果。',
      'Agent 会选择合适的模型并准备生成。',
      '获得视频、调整细节，再创作下一个版本。',
    ],
    context: [
      '用俄语，无需提示词语言',
      '像和人交谈一样与 Agent 沟通。',
      '不必学习如何编写完美提示词。用俄语解释你的想法，Agent 会把它转化为 AI 模型能够理解的任务。',
      '首页视频是静音案例。打开 Agent，创作你自己的版本。',
    ],
    groups: [
      ['你提供', '想法', '照片或视频', '参考素材', '情绪'],
      ['Agent 完成', '脚本', '场景', '提示词', '成品视频'],
    ],
    timeline: [
      '新模型，更多可能。',
      '一个 Agent，在一处使用所有新模型。',
      '持续接入新模型',
      'Agent 知道何时使用哪个模型',
      '你只管创作',
    ],
    footer: [
      '你的下一个想法',
      '你会先创作什么？',
      '打开 Agent，用俄语描述你的想法，看看它会变成什么。',
    ],
    footerLegalNote: 'RU 地区法律信息。',
    legalNavLabel: '法律信息',
    legalOffer: '服务条款',
    legalPrivacy: '隐私政策',
    legalPersonalDataConsent: 'Cookie 政策',
    clips: [
      ['霓虹音乐视频', '音乐视频'],
      ['奢华发布', '风格'],
      ['极限竞速', '动作'],
      ['主角的故事', '故事'],
      ['惊艳登场', '角色'],
      ['踩点剪辑', '剪辑'],
      ['夜色都市', '氛围'],
      ['红线', '故事'],
      ['Agent 创作视频', '成品视频'],
      ['视觉参考', '参考'],
      ['无界运动', '动态'],
      ['新世界', '氛围'],
      ['视频如何诞生', '过程'],
    ],
    stories: [
      ['霓虹音乐视频', '一个想法变成来自未来的音乐视频。'],
      ['奢华发布', '参考素材定义灯光、节奏和首映氛围。'],
      ['极限竞速', 'Agent 将速度与张力组合成完整场景。'],
      ['主角的故事', '角色与想法成为一段简短的视觉故事。'],
      ['惊艳登场', '一个瞬间成为富有个性的场景。'],
      ['踩点剪辑', '动作、音乐和转场组成充满能量的剪辑。'],
      ['夜色都市', '城市灯光定义新版本的氛围与方向。'],
      ['红线', '一个视觉母题串联起整个故事。'],
    ],
  },
  auth: {
    eyebrow: 'AI 视频创作平台',
    title: '为俄语市场制作推广素材。',
    subtitle: '将产品卖点、参考素材和创意转化为可测试的视频概念体系。',
    google: '使用 Google 继续',
    vk: '使用 VK 继续',
    emailAddress: '邮箱地址',
    verificationCode: '验证码',
    sendCode: '发送验证码',
    loginWithEmail: '使用邮箱登录',
    emailWorking: '处理中…',
    codePrinted: '验证码已打印在服务端终端。',
    emailFailed: '邮箱登录失败，请检查验证码后重试。',
    terms:
      '继续即表示您同意<termsLink>服务条款</termsLink>和<privacyLink>隐私政策</privacyLink>。',
    working: '正在打开 {{provider}} 演示登录…',
    success: '登录成功。',
    cancelled: '登录已取消。请尝试其他登录方式。',
    failed: '登录失败，请重试。',
    email: '邮箱',
    password: '密码',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
    displayName: '显示名称',
    register: '创建账号',
    login: '登录',
    registerTitle: '创建账号',
    registerSubtitle: '输入您的信息以开始使用。',
    loginTitle: '欢迎回来',
    loginSubtitle: '登录您的账号。',
    orContinueWith: '或使用以下方式继续',
    alreadyHaveAccount: '已有账号？',
    dontHaveAccount: '没有账号？',
    passwordMinLength:
      '至少 10 个字符，且强度须达到「强」。更长的密码短语更安全。',
    confirmPassword: '确认密码',
    passwordsDoNotMatch: '两次输入的密码不一致',
    passwordTooShort: '密码至少需要 10 个字符。',
    passwordTooWeak: '密码强度须达到「强」。请使用更长、更不易猜测的密码短语。',
    passwordValidationUnavailable: '暂时无法验证密码，请重试。',
    retry: '重试',
    passwordStrength: {
      score0: '非常弱',
      score1: '弱',
      score2: '一般',
      score3: '良好',
      score4: '强',
    },
    emailRequired: '请输入邮箱',
    passwordRequired: '请输入密码',
    invalidEmail: '请输入有效的邮箱地址',
    emailAlreadyExists: '该邮箱已被注册',
    invalidCredentials: '邮箱或密码错误',
    region: {
      boundary: '数据边界',
      title: '选择你的数据存储区域',
      description:
        '请在登录前选择区域。不同区域的账户和工作区相互独立，数据无法直接迁移。',
      russia: '俄罗斯',
      russiaDetail: '你的账户和工作区数据将保存在俄罗斯区域。',
      global: '国际',
      globalDetail: '你的账户和工作区数据将保存在国际区域。',
      continue: '继续使用 {{region}} →',
      connecting: '正在连接 {{region}}…',
      unavailable: '{{region}} 不可用',
      blocked: '区域连接受阻',
      verifyFailed: '无法验证所选 API。',
      noLogin: '无法发送登录请求。',
      retry: '重试',
      choose: '选择区域',
    },
  },
  navigation: {
    account: '账户',
    menu: '打开导航',
    collapseSidebar: '收起侧边栏',
    expandSidebar: '展开侧边栏',
  },
  onboarding: {
    step: '公司设置',
    title: '创建您的第一个工作空间',
    subtitle: '填写公司、主要市场和数据存储区域，以设置工作区。',
    name: '公司名称',
    website: '网站',
    industry: '行业',
    size: '公司规模',
    jobTitle: '您的职位',
    market: '主要市场',
    region: '数据区域',
    regionHint: '数据区域创建后无法更改。如需使用其他区域，请创建新的公司。',
    logo: '公司标志',
    chooseLogo: '选择文件',
    noLogoSelected: '未选择文件',
    create: '创建工作空间',
    industries: {
      ecommerce: '电子商务',
      agency: '营销代理商',
      gaming: '游戏与应用',
      brand: '消费品牌',
      other: '其他',
    },
    sizes: {
      small: '1–10',
      medium: '11–50',
      large: '51–250',
      enterprise: '250+',
    },
    markets: { ru: '俄罗斯', cis: '独联体', global: '全球' },
  },
  billing: {
    title: '套餐与账单',
    sharedBy: '{{workspace}} 的所有成员共享肥皂币。',
    sharedBalance: '共享肥皂币余额',
    credits: '肥皂币',
    choosePlan: '选择套餐',
    displayOnly: '当前价格仅供展示，支付功能尚未开放。',
    secureCheckout: '付款将通过 YooKassa 安全完成。',
    continueToPayment: '前往安全支付',
    pendingPlanPayment:
      '你有一笔 {{plan}} {{cadence}}订单尚未完成（{{amount}}）',
    annualCadence: '年付',
    monthlyCadence: '月付',
    continuePendingPayment: '继续支付旧订单',
    abandonPendingPayment: '放弃旧订单并重新选择',
    abandoningPendingPayment: '正在放弃旧订单…',
    abandonPendingPaymentError: '旧订单暂时无法放弃。请继续支付，或稍后重试。',
    openingCheckout: '正在打开安全支付…',
    annualUnavailable: '年付即将开放',
    annualAfterCurrentTerm: '当前付费周期结束后可购买年付套餐。',
    annualTermLocked: '当前年付有效期结束后可更换或重新购买套餐。',
    checkoutError: '无法发起支付，请重试。',
    insufficientCreditsTitle: '当前肥皂币不足，无法开始生成',
    insufficientCreditsNeed: '本次生成需要',
    insufficientCreditsHave: '，当前可用',
    insufficientCreditsNeedMore: '，还差',
    insufficientCreditsMoreSuffix: '。',
    requiredCreditsOnlyPrefix: '本次生成需要',
    requiredCreditsOnlySuffix: '。',
    insufficientCreditsSummary:
      '本次生成需要 {{required}} 积分，当前可用 {{available}} 积分，还差 {{shortage}} 积分。',
    requiredCreditsOnly: '本次生成需要 {{required}} 积分。',
    loadingPlans: '正在加载套餐…',
    plansLoadError: '{{message}} 请关闭弹窗后重试。',
    buyCredits: '购买肥皂币',
    getCredits: '获取肥皂币',
    workspaceCredits: '工作空间肥皂币',
    renewsOn: '肥皂币将于 {{date}} 刷新',
    creditRefreshOn: '肥皂币将于 {{date}} 刷新',
    planEndsOn: '套餐有效期至 {{date}}',
    noAutomaticRenewal: '不会自动续费',
    noScheduledRefresh: '暂无计划肥皂币刷新',
    renewYourPlan: '续订套餐',
    planExpiredTitle: '套餐已到期',
    planExpiredDescription:
      '续订套餐后即可继续生成。已购买的肥皂币仍保留在工作区，充值肥皂币不会续订套餐。',
    watermarkUpgradeDescription: '升级 Pro，即可下载无水印原图和原视频。',
    upgradeToPro: '升级 Pro',
    upgradeYourPlan: '升级套餐',
    comingSoon: '即将推出',
    creditsComingSoon:
      '肥皂币购买功能即将推出。目前可由运营人员为此工作空间添加已购肥皂币。',
    creditPackDescription: '无需更换当前套餐，直接补充肥皂币。',
    creditPackRequiresPlan: '加量包仅面向有效付费套餐开放。',
    oneTimeCreditPack: '一次性加量包',
    creditPackNoRenewal: '一次性付款，不会自动续费。',
    creditPackPaidPlanOnly: '仅在付费套餐有效期间购买。',
    buyCreditPack: '购买 1,000 肥皂币',
    current: '当前套餐',
    currentPlan: '当前套餐',
    perMonth: '每月',
    once: '一次性',
    monthly: '月付',
    yearlyDiscount: '年付，优惠 {{discount}}%',
    billingCadence: '计费周期',
    billedAnnually: '按年计费',
    annualTotal: '每年 {{price}}',
    customPricing: '定制价格',
    customCredits: '定制肥皂币',
    planNames: {
      FREE: '免费版',
      STANDARD: '标准版',
      PRO: '专业版',
      MAX: '旗舰版',
      ENTERPRISE: '企业版',
    },
    badges: { POPULAR: '热门', BEST_VALUE: '最具性价比' },
    entitlements: {
      CORE_AGENT: 'AI 创意 Agent',
      IMAGE_AND_VIDEO_TOOLS: '图片与视频生成工具',
      PRIVATE_ASSET_LIBRARY: '工作空间私有素材库',
      ECONOMY_VIDEO_MODEL: 'Мыло 视频 2.0 Mini 经济模型，最高 720p',
      INCLUDES_FREE: '包含免费版全部能力',
      ALL_PRODUCTION_MODELS: '全部已上线生产模型',
      MONTHLY_CREDIT_REFRESH: '肥皂币每月刷新',
      CREDIT_PACK_PURCHASES: '可购买肥皂币加量包',
      VIDEO_UP_TO_1080P: '全部 Мыло 视频模型，最高 1080p',
      INCLUDES_STANDARD: '包含标准版全部能力',
      HIGHER_MONTHLY_GENERATION_ALLOWANCE: '每月更多生成次数',
      API_ACCESS: '开发者 API 使用权限',
      WATERMARK_FREE_VIDEOS: '视频无水印下载',
      WATERMARK_FREE_IMAGES: '图片无水印下载',
      WATERMARK_FREE_VIDEO_EDITOR_EXPORTS: '视频编辑器导出无水印',
      VIDEO_UP_TO_4K: '4K 视频生成',
      INCLUDES_PRO: '包含专业版全部能力',
      HIGHEST_MONTHLY_GENERATION_ALLOWANCE: '最高月度生成额度',
      ONE_MONTH_CREDIT_ROLLOVER: '未用肥皂币可滚存一个月',
      INCLUDES_MAX: '包含旗舰版全部能力',
      CUSTOM_CREDITS: '自定义肥皂币数量',
      CUSTOM_TEAM_SIZE: '自定义成员数量',
      ENTERPRISE_SUPPORT: '企业开通与支持',
    },
    includedCreditsLabel: '包含',
    includedCredits: '包含积分：{{count}}',
    upgradePlan: '升级套餐',
    creditTimeline: '肥皂币时间线',
    timelineHint: '查看哪些共享肥皂币将优先到期。',
    noGrants: '暂无肥皂币记录。',
    expires: '{{date}} 到期',
    neverExpires: '永久有效',
    planCredits: '套餐肥皂币',
    carriedCredits: '结转肥皂币',
    purchasedCredits: '购买肥皂币',
    promotionalCredits: '赠送肥皂币',
    existingCredits: '原有肥皂币',
    checkoutSoon:
      '支付功能即将开放。目前可由运营人员调整工作空间套餐和肥皂币。',
    chooseWorkspace: '请选择工作空间后查看账单。',
    loading: '正在加载账单…',
    loadError: '无法加载账单',
    loadErrorHint: '请刷新页面重试。',
    creditUsage: '肥皂币使用明细',
    creditUsageHint: '查看此工作空间的每一笔肥皂币变动。',
    activityTypeFilter: '变动类型',
    allActivityTypes: '全部变动',
    activityLoading: '正在加载积分明细…',
    activityLoadError: '无法加载积分明细，请重试。',
    noActivity: '暂无积分变动。',
    previous: '上一页',
    next: '下一页',
    activityColumns: {
      date: '时间',
      event: '事件',
      type: '类型',
      change: '积分变动',
      status: '状态',
    },
    activityTypes: {
      GENERATION: '生成消耗',
      TOP_UP: '充值',
      PLAN_GRANT: '套餐发放',
      CARRYOVER: '积分结转',
      PROMOTION: '赠送积分',
      REFUND: '退款',
      ADMIN_ADJUSTMENT: '人工调整',
      EXPIRY: '积分过期',
      OTHER: '其他',
    },
    activityStatuses: {
      COMPLETED: '已完成',
      HELD: '已冻结',
      RELEASED: '已释放',
    },
    credit: {
      topUp: '积分已充值',
      generationHeld: '生成任务冻结积分',
      labUsed: 'Mylo lab 已扣除肥皂币',
      generationUsed: '生成任务已扣除积分',
      generationReleased: '已退回预留积分',
      refund: '积分已退回',
      adminAdjustment: '积分人工调整',
      planGrant: '套餐积分已发放',
      carryover: '积分已结转',
      promotion: '赠送积分已发放',
      existingBalance: '原有积分已导入',
      expired: '积分已过期',
      merchantAdjustment: '商户积分调整',
      other: '积分变动',
    },
  },
  developerApi: {
    eyebrow: '工作空间开发者能力',
    title: 'MyloStudio API',
    description:
      '为 ({{workspace}}) 创建服务端密钥，使用工作空间积分调用生图和生视频能力。',
    activeKeys: '{{count}} 个有效密钥',
    proBenefit: '专业版权益',
    lockedTitle: '使用 MyloStudio API 构建应用',
    lockedDescription:
      '升级至专业版或更高套餐，即可创建 API 密钥，并通过开发者 API 使用工作空间积分。',
    upgradeToPro: '升级至专业版',
    keyName: '密钥名称',
    keyNamePlaceholder: '生产服务器…',
    expiresAt: '到期时间（可选）',
    permissions: '权限',
    createKey: '创建 API 密钥',
    creating: '创建中…',
    keysTitle: 'API 密钥',
    maxKeys: '最多 5 个有效密钥',
    loading: '正在加载 API 密钥…',
    emptyTitle: '还没有 API 密钥',
    emptyDescription: '填写使用该密钥的系统名称，然后在上方创建。',
    active: '有效',
    expired: '已过期',
    revoked: '已撤销',
    keyMeta: '{{creator}} 创建于 {{date}} · 最近使用 {{lastUsed}}',
    never: '从未',
    revoke: '撤销',
    revokeConfirm: '撤销“{{name}}”？正在使用它的应用会立即停止工作。',
    quickStart: '快速开始',
    quickStartDescription:
      '只在服务端保存密钥，不要把它暴露在浏览器代码或 URL 中。',
    viewApiDocs: '查看 API 文档',
    imageExample: '图片生成',
    videoExample: '视频生成',
    createdTitle: '复制你的 API 密钥',
    createdDescription:
      '这个密钥只显示一次。关闭前，请把它保存到服务端密钥管理工具中。',
    copyKey: '复制密钥',
    copied: '已复制',
    done: '完成',
    chooseWorkspace: '请选择要管理 API 密钥的工作空间。',
    loadError: '无法加载 API 密钥。',
    createError: '无法创建 API 密钥。',
    revokeError: '无法撤销 API 密钥。',
  },
  account: {
    title: '账户设置',
    workspaceSettings: '工作空间设置',
    workspaceSettingsTab: '设置',
    workspaceMembersTab: '成员',
    workspaceBillingTab: '套餐与账单',
    workspaceEyebrow: '工作空间管理',
    workspaceSettingsDescription: '查看工作空间并维护团队共享的基础资料。',
    workspaceDetails: '工作空间资料',
    workspaceDetailsHint: '所有者和管理员可以修改团队共享的工作空间资料。',
    workspaceName: '工作空间名称',
    workspaceNameRequired: '请输入工作空间名称。',
    workspaceUpdated: '工作空间已更新',
    workspaceUpdateFailed: '无法更新工作空间，请重试。',
    workspaceReadOnly: '只有所有者和管理员可以修改此工作空间。',
    currentWorkspace: '当前工作空间',
    noWorkspaces: '暂无可用的工作空间。',
    createNewWorkspace: '新建工作空间',
    createWorkspace: '创建工作空间',
    manageWorkspaces: '管理工作空间',
    regionLabel: '区域 {{region}}',
    activeRegionName: '内测版',
    close: '关闭',
    createWorkspaceFailed: '无法创建工作空间，请重试。',
    switchWorkspaceFailed: '无法切换工作空间，请重试。',
    plansAndBilling: '套餐与账单',
    accountProfile: '账户资料',
    profileDescription: '管理账户展示信息，并查看当前关联的登录方式。',
    changeAvatar: '更换头像',
    avatarHint: '支持 JPG、PNG 或 WebP，最大 5 MB。',
    uploadingAvatar: '上传中 {{progress}}%',
    avatarTypeError: '请选择 JPG、PNG 或 WebP 图片。',
    avatarSizeError: '请选择小于 5 MB 的图片。',
    avatarUploadFailed: '头像上传失败，请重试。',
    avatarUpdated: '头像已更新',
    userId: '用户 ID',
    copyUserId: '复制用户 ID',
    copied: '已复制',
    signInMethods: '登录方式',
    signInMethodsHint: '与 Mylo 账号关联的登录方式。',
    connected: '已关联',
    notConnected: '未关联',
    saveChanges: '保存更改',
    saving: '保存中…',
    changesSaved: '更改已保存',
    saveFailed: '无法保存更改，请重试。',
    displayNameRequired: '请输入显示名称。',
    upgradePlan: '升级套餐',
    developerApi: '开发者 API',
    inviteMembers: '邀请成员',
    referFriend: '推荐给朋友',
    workspace: '工作空间',
    theme: '主题',
    themeOptions: '主题选项',
    languageOptions: '语言选项',
    system: '跟随系统',
    light: '浅色',
    dark: '深色',
    switchWorkspace: '切换工作空间',
    logOut: '退出登录',
    profile: '个人资料',
    security: '安全设置',
    companies: '公司',
    members: '成员管理',
    language: '语言',
    preferences: '偏好设置',
    wallet: '钱包',
    displayName: '显示名称',
    email: '电子邮箱',
    provider: '登录方式',
    providers: '登录方式',
    current: '当前',
    switchCompany: '切换公司',
    addCompany: '添加公司',
    dataRegion: '数据区域',
    appearance: '外观',
    notifications: '生成通知',
    resetHint: '清除本地演示会话、公司、项目和素材。',
    confirmReset: '重置演示会话、公司、项目和素材？',
    balance: '余额',
    credits: '积分',
    currency: '货币',
    creditRecords: '积分记录',
    noCreditRecords: '暂无积分记录。',
    filterType: '变动类型',
    filterDateFrom: '开始日期',
    filterDateTo: '结束日期',
    allTypes: '全部类型',
    resetFilter: '重置',
    applyFilter: '筛选',
    ledgerType: {
      TOP_UP: '充值',
      HOLD: '预留',
      CAPTURE: '扣费',
      RELEASE: '解除预留',
      REFUND: '退款',
      ADMIN_ADJUSTMENT: '管理员调整',
      SUBSCRIPTION_CHARGE: '订阅扣费',
      PROMOTIONAL_CREDIT: '赠送积分入账',
      EXPIRED_CREDIT: '积分过期',
      MERCHANT_CREDIT_ADJUSTMENT: '商家积分调整',
    },
    topUp: '充值',
    transactionType: '类型',
    transactionAmount: '金额',
    transactionDate: '日期',
    linkedAccounts: '已关联账号',
    unlink: '解绑',
    link: '绑定',
    activeDevices: '活跃设备',
    thisDevice: '当前设备',
    unknownDevice: '未知设备',
    expires: '过期时间',
    revoke: '结束会话',
    dangerZone: '危险操作',
    deleteWorkspace: '删除工作空间',
    deleteWorkspaceHint: '工作空间可在 30 天内恢复。',
    role: '角色',
    owner: '所有者',
    admin: '管理员',
    member: '成员',
    personalInfo: '个人信息',
    connectedAccounts: '已关联账号',
    workspaceList: '工作空间列表',
    selectWorkspace: '请选择工作空间。',
    yourRole: '你的角色是{{role}}。',
    regionLocked: '数据存储区域：{{region}}，无法更改。',
    you: '你',
    vkIdentity: 'VK 身份',
    transferOwnership: '转让所有权',
    remove: '移除',
    invitePeople: '邀请成员',
    inviteExpiryHint: '邀请链接将在七天后过期，且只能使用一次。',
    invitationRole: '新成员角色',
    createSecureLink: '创建安全链接',
    invitationLink: '邀请链接',
    copy: '复制',
    pending: '待接受',
    accepted: '已接受',
    revoked: '已撤销',
    expired: '已过期',
    transferOwnershipTitle: '转让所有权？',
    removeMemberTitle: '移除成员？',
    revokeInvitationTitle: '撤销邀请？',
    transferOwnershipDescription: '将此工作空间转让给 {{name}}？',
    removeMemberDescription: '将 {{name}} 移出此工作空间？',
    revokeInvitationDescription: '此邀请链接将立即失效。',
    cancel: '取消',
    confirm: '确认',
    membersLoadError: '无法加载工作空间成员。',
    deviceExpires: '会话将于 {{date}} 结束',
    deletedWorkspaces: '已删除的工作空间',
    deletedWorkspacesHint: '所有者可在 30 天恢复期内恢复工作空间。',
    purgeDate: '{{date}} 永久删除',
    purgePending: '等待永久删除',
    restore: '恢复',
    noDeletedWorkspaces: '没有已删除的工作空间。',
    joiningWorkspace: '正在加入工作空间…',
    inviteAcceptError: '无法接受邀请。',
  },
  validation: {
    required: '此字段为必填项。',
    website: '请输入有效的网站地址。',
    logoSize: '请选择小于 5 MB 的图片。',
  },
  studio: {
    campaignBriefForm: {
      targetAudience: '目标受众',
      platforms: '投放平台',
      campaignGoal: '活动目标',
      keyMessage: '核心信息',
      tone: '语气/风格',
      cta: '行动号召',
      constraints: '限制条件',
      targetAudiencePlaceholder: '例如：都市职场人士，25-34 岁',
      platformsPlaceholder: '例如：Instagram Reels、TikTok',
      campaignGoalPlaceholder: '例如：新品上市曝光',
      keyMessagePlaceholder: '例如：7 天可见透亮肌肤',
      tonePlaceholder: '例如：干净、高级、自信',
      ctaPlaceholder: '例如：立即购买套装',
      constraintsPlaceholder: '例如：避免医疗宣称，保持白色包装',
      custom: '自定义',
      customLabel: '自定义{{field}}',
      targetAudienceQuestion: '这组投放图要说服谁？',
      platformsQuestion: '这些静态素材要投放到哪里？',
      campaignGoalQuestion: '这组图片广告要达成什么目标？',
      closeTool: '关闭表单',
      previousQuestion: '上一题',
      nextQuestion: '下一题',
      required: '必填',
      requiredHint: '完成必填问题后即可继续。',
      skip: '跳过',
      stepOf: '/',
      typeOwnAnswer: '输入你自己的答案',
      targetAudienceOptionGenZ: 'Z 世代潮流人群',
      targetAudienceOptionGenZHint: '关注社交视觉趋势和种草内容',
      targetAudienceOptionProfessionals: '都市职场人群',
      targetAudienceOptionProfessionalsHint: '强调高品质日常使用场景',
      targetAudienceOptionParents: '新手父母',
      targetAudienceOptionParentsHint: '突出实用证明和清晰产品卖点',
      targetAudienceOptionFitness: '健身爱好者',
      targetAudienceOptionFitnessHint: '强调性能对比和高端质感',
      platformsOptionInstagram: 'Instagram 图文 + Stories',
      platformsOptionInstagramHint: '适配 Instagram 动态和 Stories 的素材',
      platformsOptionTikTok: 'TikTok 图片帖',
      platformsOptionTikTokHint: '让产品信息一目了然的图片',
      platformsOptionXhs: '小红书笔记',
      platformsOptionXhsHint: '用于介绍产品并直观展示优势和细节的素材',
      platformsOptionMultiStatic: '多平台静态投放',
      platformsOptionMultiStaticHint: '适用于自然内容和付费推广的通用素材',
      campaignGoalOptionLaunch: '新品发布',
      campaignGoalOptionLaunchHint: '介绍产品并制造第一波需求',
      campaignGoalOptionConversions: '推动转化',
      campaignGoalOptionConversionsHint: '引导用户购买或注册',
      campaignGoalOptionBenefits: '解释卖点',
      campaignGoalOptionBenefitsHint: '让核心价值清楚、好记',
      campaignGoalOptionRetarget: '再营销',
      campaignGoalOptionRetargetHint: '召回感兴趣的受众并推动他们采取行动',
      submit: '创建视觉概念',
    },
    videoBriefForm: {
      targetAudience: '目标受众',
      platforms: '投放平台',
      campaignGoal: '活动目标',
      hook: 'Hook（前 3 秒）',
      keyMessage: '核心信息',
      tone: '语气/风格',
      cta: '行动号召',
      constraints: '限制条件',
      targetAudiencePlaceholder: '例如：都市职场人士，25-34 岁',
      platformsPlaceholder: '例如：TikTok、Instagram Reels',
      campaignGoalPlaceholder: '例如：新品上市曝光',
      hookPlaceholder: '例如：产品硬切 + 大胆主张',
      keyMessagePlaceholder: '例如：更轻便、更耐用、更有质感',
      tonePlaceholder: '例如：动感、高级、真实',
      ctaPlaceholder: '例如：立即体验',
      constraintsPlaceholder: '例如：保持 Logo 清晰，避免价格承诺',
      custom: '自定义',
      customLabel: '自定义{{field}}',
      targetAudienceQuestion: '这支广告要说服谁？',
      platformsQuestion: '这支视频要投放到哪里？',
      campaignGoalQuestion: '这支视频要达成什么目标？',
      closeTool: '关闭表单',
      previousQuestion: '上一题',
      nextQuestion: '下一题',
      required: '必填',
      requiredHint: '完成必填问题后即可继续。',
      skip: '跳过',
      stepOf: '/',
      typeOwnAnswer: '输入你自己的答案',
      targetAudienceOptionGenZ: 'Z 世代潮流人群',
      targetAudienceOptionGenZHint: '关注创作者内容和短视频趋势',
      targetAudienceOptionProfessionals: '都市职场人群',
      targetAudienceOptionProfessionalsHint: '强调高品质日常使用场景',
      targetAudienceOptionParents: '新手父母',
      targetAudienceOptionParentsHint: '突出省时、实用和清晰卖点',
      targetAudienceOptionFitness: '健身爱好者',
      targetAudienceOptionFitnessHint: '强调性能对比和高端质感',
      platformsOptionTikTok: 'TikTok',
      platformsOptionTikTokHint: '醒目的开场和自然的 TikTok 节奏',
      platformsOptionReels: 'Instagram Reels',
      platformsOptionReelsHint: '在日常场景中直观展示产品',
      platformsOptionShorts: 'YouTube Shorts',
      platformsOptionShortsHint: '适合清晰演示和搜索场景',
      platformsOptionAllShorts: 'TikTok + Reels + Shorts',
      platformsOptionAllShortsHint: '一条竖版短视频适配多平台',
      campaignGoalOptionLaunch: '新品发布',
      campaignGoalOptionLaunchHint: '介绍产品并制造第一波需求',
      campaignGoalOptionConversions: '推动转化',
      campaignGoalOptionConversionsHint: '引导观众购买或注册',
      campaignGoalOptionBenefits: '解释卖点',
      campaignGoalOptionBenefitsHint: '让核心价值清楚、好记',
      campaignGoalOptionRetarget: '再营销',
      campaignGoalOptionRetargetHint: '召回感兴趣的受众并推动购买',
      submit: '创建视频概念方案',
    },
    promptBox: {
      dropMedia: '拖放图片或视频到此处',
      placeholder:
        '从你的想法开始。输入 @ 引用素材，或输入 / 使用智能助手技能。',
    },
    conceptPicker: {
      title: '选择概念板',
      description: '在图库中预览概念图，然后在此选择方向。',
      generatedImages: '已生成 {{count}} 张图片',
      generatingImages: '正在生成 {{count}} 张图片…',
      generatedDirections: '已生成 {{count}} 个创意方向',
      generatedImage: '生成图片',
      imageLabel: '图片 {{index}}',
      directionLabel: '方向 {{index}}',
      chooseDirection: '选择此方向',
      ready: '已就绪',
      generating: '生成中…',
      expandItem: '展开 {{label}}',
      collapseItem: '收起 {{label}}',
      previewImage: '在图库中预览 {{label}}',
      useConceptImage: '使用概念图 {{index}}：{{title}}',
      useConceptDirection: '使用方向 {{index}}：{{title}}',
      useVideoImage: '使用图片 {{index}} 生成视频',
    },
    finalImagesTool: {
      title: '最终效果图',
      description_one: '同风格的 {{count}} 张高精度投放图',
      description_other: '同风格的 {{count}} 张高精度投放图',
      generating: '正在生成 3 张高精度图片…',
      concept: '基于概念：{{title}}',
    },
    gallery: {
      hideWatermark: '隐藏水印',
      watermarkAllResources: '应用到所有生成图片和视频',
      preparingDownload: '准备中…',
      downloadFailed: '下载失败，请重试。',
      generations: '结果',
      cuts: '剪辑',
      back: '返回阶段视图',
      generationStages: '生成阶段',
      filter: '筛选图库',
      layout: '切换布局',
      addToFavorites: '添加到收藏',
      removeFromFavorites: '取消收藏',
      moreActions: '更多操作',
      selectAsset: '选择素材',
      addToCut: '添加到剪辑',
      download: '下载',
      copyLink: '复制链接',
      delete: '删除',
      loading: '正在加载图库…',
      loadingHint: '正在从存储中获取会话素材。',
      emptyTitle: '暂无结果',
      emptyHint: '上传产品图或生成广告素材后，会按阶段显示在左侧图库中。',
      generatedDialogueAudio: '生成的对白音频',
    },
    videoJobTool: {
      title: '正在创建广告视频',
      description: '正在根据参考图片和视频生成新视频',
      progressLabel: '预计视频生成进度',
      percent: '预计进度 {{progress}}%',
      canvasHint: '完成后会自动显示在当前舞台。',
      direction: '方向：{{title}}',
      job: '任务 {{id}}',
      status: {
        queued: '视频任务正在排队…',
        processing: '正在渲染广告视频…',
        succeeded: '视频已完成。',
        failed: '视频生成失败。',
        cancelled: '视频生成已取消。',
      },
    },
    navigation: {
      home: '首页',
      agent: '智能助手',
      generate: '生成',
      assets: '素材',
      primary: '主导航',
      mobile: '移动端工作室导航',
      close: '关闭导航',
      myloLab: 'Mylo lab',
    },
    shell: {
      currentRegion: '当前区域：{{region}}',
      switchRegion: '切换区域？',
      switchRegionDescription:
        '切换区域后需要重新登录，工作区数据不会在不同区域之间迁移。',
      cancel: '取消',
      switch: '切换区域',
    },
    home: {
      greeting: '{{name}}，你好，想创作什么？',
      getStarted: '开始创作',
      presetCategories: '预设分类',
      creativeShortcuts: '创作快捷方式',
      creativePresets: '创作预设',
      allSkills: '全部技能',
      shortcuts: {
        editTimeline: '前往剪辑',
        seedance: 'Мыло 视频 2.5',
        doubao: '豆包',
        adCampaign: '广告营销',
        moodBoard: '情绪板',
      },
      tabs: {
        marketing: '营销活动',
        movies: '影视',
        social: '社交媒体',
        education: '教育内容',
        experimental: '实验艺术',
        other: '其他',
      },
      presets: {
        adapt: '将一个创意适配到所有平台',
        posts: '创建一周的社交内容',
        remix: '用我的产品改编热门趋势',
        thumbnails: '生成一组缩略图',
        reel: '制作竖屏短视频',
      },
    },
    assets: {
      eyebrow: '工作区素材库',
      title: '素材',
      description: '在不同智能助手会话中复用图片、视频和音频。',
      upload: '上传',
      uploading: '正在上传 · {{count}}',
      search: '搜索素材',
      searchPlaceholder: '搜索素材…',
      sessionGenerations: '此会话生成的内容',
      sessionGenerationsError: '无法加载此会话生成的内容，请重试。',
      sessionGenerationsEmptyTitle: '此会话还没有生成内容',
      sessionGenerationsEmptyDescription:
        '生成的图片、视频和概念会显示在这里。',
      showAll: '显示全部素材',
      retry: '重试',
      tryAgain: '请重试或选择其他素材。',
      errors: {
        deleteForbidden: '只有素材创建者或工作区管理员可以删除该素材。',
        deleteInUse:
          '该素材仍被生成记录或项目使用，请先从对应位置移除后再删除。',
        deleteMissing: '该素材已不可用，请刷新素材库后重试。',
        deleteFailed: '无法删除该素材，请重试。',
        favoriteFailed: '无法更新收藏状态，请重试。',
      },
      deleteConfirm: '删除 {{name}}？',
      deleteDescription: '这会将 {{name}} 从工作区素材库中移除。',
      cancel: '取消',
      emptyTitle: '还没有素材',
      emptyDescription: '上传图片、视频或音频文件。',
      select: '选择 {{name}}',
      preview: '预览 {{name}}',
      loadingMore: '正在加载更多…',
      retryLoadingMore: '重新加载更多素材',
      retryLoading: '重新加载素材',
      pickerDescription: '将工作区素材添加到这条消息。',
      uploadMedia: '上传媒体',
      dropOrUpload: '拖放或上传文件',
      mediaTypes: '图片、视频、音频',
      image: '图片',
      video: '视频',
      audio: '音频',
      loadingVideo: '正在加载视频…',
      file: '文件',
      delete: '删除',
      editVideo: '编辑视频',
      deleteLabel: '删除 {{name}}',
      editVideoLabel: '编辑视频 {{name}}',
      uploadActivity: '上传进度',
      uploads: '上传任务',
      activeUploads: '{{count}} 个进行中',
      retryUpload: '重试上传 {{name}}',
      dismissUpload: '移除上传任务 {{name}}',
      status: {
        queued: '排队中',
        preparing: '准备中',
        uploading: '上传中',
        complete: '已完成',
        failed: '失败',
      },
    },
    videoEditor: {
      includeWatermark: '保留水印',
      chooseTitle: '选择要编辑的视频',
      chooseDescription: '选择工作区中的视频，创建时间线并开始编辑。',
      opening: '正在打开 {{name}}…',
      openError: '无法在编辑器中打开此视频，请选择其他视频。',
      videoAssets: '视频素材',
      searchAssets: '搜索素材',
      searchAssetsPlaceholder: '搜索素材…',
      uploadFiles: '拖放或上传文件',
      loadingAssets: '正在加载素材',
      timeline: '视频时间线',
      playhead: '播放头',
      clip: '片段',
      preparingMedia: '正在准备媒体…',
      preview: '视频预览',
      playPreview: '播放预览',
      pausePreview: '暂停预览',
      tools: '编辑工具',
      undo: '撤销',
      redo: '重做',
      split: '拆分',
      mute: '静音',
      unmute: '取消静音',
      deleteClip: '删除片段',
      clipActions: '片段操作',
      editClip: '编辑片段',
      splitShots: '拆分镜头',
      splitAudio: '拆分音频',
      splitAudioUnavailable: '暂不支持独立音频轨道',
      rippleDelete: '波纹删除',
      all: '全部',
      uploads: '已上传',
      folders: '文件夹',
      favorites: '收藏',
      addFavorite: '收藏 {{name}}',
      removeFavorite: '取消收藏 {{name}}',
      recents: '最近使用',
      supportedMedia: '图片、视频、PDF、音频',
      noVideoAssets: '还没有视频素材',
      uploadClipHint: '上传一个视频片段即可开始。',
      selectClip: '选择片段 {{id}}',
      trimStart: '裁剪片段 {{id}} 的开头',
      trimEnd: '裁剪片段 {{id}} 的结尾',
      muteClip: '将片段静音',
      unmuteClip: '取消片段静音',
      loadError: '无法加载视频项目。请返回素材库并重新打开源视频。',
      exportUnavailable: '无法获取导出状态',
      exportRevision: '导出新版本',
      exported: '已导出',
      exporting: '正在导出 {{progress}}%',
      export: '导出',
      back: '返回',
      projectName: '项目名称',
      renameFailed: '重命名失败',
      saving: '正在保存…',
      saveFailed: '保存失败',
      retrySave: '重试保存',
      newerVersion: '有更新版本可用',
      saved: '已保存',
      retryExport: '重试失败的导出',
      downloadMp4: '下载 MP4',
      download: '下载',
      closePanel: '关闭侧边栏',
      openPanel: '打开侧边栏',
      recoveredDraft: '已恢复的视频草稿',
      recoveryAvailable: '此浏览器中有尚未保存的编辑。',
      restoreEdits: '恢复编辑',
      discard: '不恢复',
      addAssetFailed: '无法添加该素材。请检查文件后重试。',
      chooseAssetAgain: '重新选择素材',
      createCut: '新建剪辑',
      generations: '结果',
      sessionGenerationsEmpty: '本轮生成内容将显示在这里',
      cuts: '剪辑',
      newCut: '新版本',
      creatingCut: '正在创建…',
      createCutFailed: '无法新建剪辑，请重试。',
      emptyCut: '添加素材以开始这个剪辑',
      selectOrDropMedia: '选择或拖入视频、音频或图片',
      cutActions: '{{name}} 的操作',
      renameCut: '重命名',
      duplicateCut: '复制',
      deleteCut: '删除',
      deleteCutConfirmTitle: '删除这个版本？',
      deleteCutConfirmDescription: '“{{name}}”将被永久删除，此操作无法撤销。',
      cancel: '取消',
      assetDialogTitle: '添加素材',
      closeAssetDialog: '关闭素材选择器',
      noFavoriteAssets: '还没有收藏的素材',
      noMediaAssets: '暂无素材',
      keyboardShortcuts: '键盘快捷键',
      shortcuts: {
        playbackNavigation: '播放与导航',
        playPause: '播放 / 暂停',
        skipBack: '后退 5 秒',
        skipForward: '前进 5 秒',
        stepBack: '后退 1 帧',
        stepForward: '前进 1 帧',
        previousEdge: '上一个片段边缘',
        nextEdge: '下一个片段边缘',
        jumpStart: '跳到开头',
        jumpEnd: '跳到结尾',
        zoomIn: '放大时间线',
        zoomOut: '缩小时间线',
        selecting: '选择',
        selectAll: '选择所有片段',
        selectAcross: '跨所有轨道框选',
        selectTrack: '在单个轨道框选',
        markStart: '标记选择起点',
        markEnd: '标记选择终点',
        clearSelection: '清除选择',
        editing: '编辑',
        split: '在播放头处分割',
        copy: '复制所选片段',
        paste: '粘贴到播放头',
        deleteGap: '删除并保留空隙',
        rippleDelete: '删除并向左补位',
        undo: '撤销',
        redo: '重做',
        duplicate: '拖动时复制片段',
      },
      shortcutPlay: '播放 / 暂停',
      shortcutSkip: '前后跳转 5 秒',
      shortcutFrame: '前后移动一帧',
      shortcutEdge: '上一个 / 下一个片段边缘',
      shortcutSplit: '在播放头处拆分',
      shortcutDelete: '删除并保留空隙',
      shortcutRippleDelete: '删除并向左补位',
      shortcutHistory: '撤销 / 重做',
      zoomOut: '缩小',
      zoomIn: '放大',
      fitTimeline: '适配时间线视图',
      addClip: '添加片段',
      preparingMetadata: '正在准备视频信息…',
      mobileEditingHint:
        '拖动和边缘裁剪仅在桌面端可用；预览、导出和工具栏命令仍可在此使用。',
      assets: '素材',
      agent: '智能助手',
      editWithAgent: '使用智能助手编辑',
      agentDescription: '描述你想用这些素材构建的视频顺序。',
      sendMessage: '发送消息',
      sendMessagePlaceholder: '发送消息…',
    },
    agentUi: {
      defaultChat: '智能助手会话',
      justNow: '刚刚',
      recents: '最近会话',
      recentChats: '最近会话',
      recentChatsLabel: '最近的智能助手会话',
      allChats: '全部会话',
      viewAllChats: '查看全部会话',
      closeChatHistory: '关闭会话记录',
      loadingChats: '正在加载会话…',
      loadingMoreChats: '正在加载更多会话…',
      retryLoadingMoreChats: '重新加载更多会话',
      sessionGenerating: '正在生成',
      sessionQueued: '排队中，尚未开始生成',
      sessionUnread: '未读',
      sessionAwaitingUser: '等待你的回答',
      noChats: '暂无会话，开始新会话后会显示在这里。',
      sessionActions: '会话操作',
      sessionActionsFor: '{{title}} 的操作',
      chatActions: '会话操作',
      newChat: '新的会话',
      newChatTooltip: '新建会话',
      rename: '重命名',
      delete: '删除',
      seeGenerationsInAssets: '在素材库中查看生成内容',
      deleteSession: '删除会话',
      deleteSessionConfirm: '删除“{{title}}”？',
      deleteSessionDescription: '这会将该会话从最近会话中移除，且无法撤销。',
      deleteSessionFailed: '无法删除此会话，请重试。',
      renameChat: '重命名会话',
      chatTitle: '会话标题',
      titleRequired: '请输入标题',
      renameFailed: '无法重命名此会话',
      cancel: '取消',
      save: '保存',
      skills: '技能',
      creationMode: '创作类型',
      agentMode: '智能助手模式',
      imageGeneration: '图片生成',
      videoGeneration: '视频生成',
      model: '模型',
      aspectRatio: '画幅',
      smartAspectRatio: '智能',
      resolution: '清晰度',
      quantity: '数量',
      duration: '时长',
      seconds: '秒',
      generateAudio: '生成音频',
      quoteFailed: '无法计算预计消耗',
      quoteRequiresPlan: '所选模型需要有效的付费套餐，请订阅或续订套餐后继续。',
      viewPlans: '查看套餐',
      retryQuote: '重新报价',
      quoteUnavailable: '报价失败',
      estimatedCredits: '预计消耗',
      calculatingCredits: '计算中…',
      videoReferenceMode: '参考方式',
      allReference: '全能参考',
      firstFrame: '首帧',
      videoContinuation: '视频续拍',
      imagePromptPlaceholder: '上传参考图或描述你想生成的图片…',
      videoPromptPlaceholder: '上传参考素材或描述你想生成的视频…',
      directCreationProgress: {
        preparing: {
          title: '正在准备生成',
          detail: '正在整理创作参数',
        },
        queued: {
          title: '已提交，排队中',
          detail: '任务即将开始',
        },
        processing: {
          title: '正在生成',
          detail: '可继续浏览，完成后会显示在这里',
        },
      },
      toolCalls: {
        recognizeImage: '正在分析图片…',
        recognizeVideo: '正在分析视频…',
        updateImage: '正在生成更新后的图片…',
        askUser: '正在打开信息表单…',
        calling: '正在调用 {{name}}…',
        updatedImage: '已生成更新后的图片。',
      },
      attachmentErrors: {
        selectWorkspaceImages: '请先选择工作区再添加图片。',
        selectWorkspaceMedia: '请先选择工作区再添加素材。',
        imageLoad: '无法加载这张图片，请重新选择。',
        videoLoad: '无法加载这个视频，请重新选择。',
        limit: '最多可添加 {{count}} 个素材。',
      },
      loadingChat: '正在加载智能助手会话…',
      skill: {
        timeline: {
          label: '时间线',
          description: '从概念到最终交付，规划各个里程碑。',
        },
        adCampaign: {
          label: '广告营销',
          description: '为每个平台从概念到成片打造视频和图片广告素材。',
        },
        adVideo: {
          label: '广告视频',
          description: '用吸引人的开场和行动号召创建短视频广告。',
        },
        moodBoard: {
          label: '情绪板',
          description: '用聚焦的视觉方向塑造你的项目。',
        },
        motionGraphic: {
          label: '动态图形',
          description: '将文本、图表和图形转化为动画概念。',
        },
      },
      assets: '素材',
      gallery: '会话图库',
      galleryTitle: '图库',
      galleryDescription: '汇集本次会话中上传、参考、构思和生成的图片。',
      galleryLoading: '正在加载图库…',
      galleryEmpty: '暂无图片',
      galleryLoadingDescription: '正在从存储中获取会话素材。',
      galleryEmptyDescription:
        '上传产品照片或生成营销视觉图，图片会作为相册展示在这里，对话仍保留在右侧。',
      assetKind: {
        upload: '上传',
        reference: '参考',
        concept: '构思',
        generated: '生成',
      },
      generatedVideo: '生成的广告视频',
      generatedImage: '生成的图片',
      galleryLabels: {
        audio: '音频',
        upload: '上传',
        uploads: '已上传',
        video: '视频',
        reference: '参考',
        references: '参考素材',
        productReference: '产品参考',
        concepts: '构思',
        generated: '已生成',
        generatedImage: '生成的图片',
        generatedVideo: '生成的视频',
      },
      previewImage: '预览图片',
      previewAsset: '预览 {{name}}',
      contextAssetUnavailable: '预览暂不可用',
      playMedia: '播放 {{name}}',
      copyMessage: '复制消息',
      messageCopied: '已复制',
      stopGenerating: '停止生成',
      sendMessage: '发送消息',
      removeAttachment: '移除 {{name}}',
      clear: '清除',
      clearComposer: '清除提示词和引用资源',
      attachment: '附件',
      addMoreMedia: '继续添加图片或视频',
      uploadMedia: '上传图片或视频',
      references: '引用素材',
      renameReference: '重命名 {{name}}',
      creativeShortcuts: '创作快捷方式',
      creativePresets: '创作预设',
      agentPrompt: '智能助手提示词',
      openAssets: '打开素材库',
      openSkills: '打开技能',
      composerTitle: '想让智能助手帮你创作什么？',
      composerDescription: '使用智能助手创作、分析并规模化生产内容。',
      generationSettings: '生成设置',
      generationSettingsDescription: '选择智能助手何时以及如何生成。',
      whenGenerating: '生成媒体时',
      optimizeGenerations: '生成偏好',
      generationAsk: '需要确认',
      generationAuto: '自动',
      generationQuality: '质量',
      generationSpeed: '速度',
      generationCost: '成本',
      generationCustom: '自定义',
      freePlanGenerationLimit:
        '免费版限制：图片偏好仍会生效；视频模型受限，最高 720p，且同时只能运行 1 个视频任务。',
      autoCreditLimit: '单次自动执行上限',
      customGenerationInstructions: '自定义偏好',
      customGenerationPlaceholder: '偏好的模型、分辨率、画幅或预算…',
      setGenerationDefault: '设为新会话默认值',
      savingGenerationDefault: '正在保存默认值…',
      planTitle: '智能助手计划',
      planThinking: '正在规划接下来的步骤…',
      planStepCount_one: '{{count}} 个计划步骤',
      planStepCount_other: '{{count}} 个计划步骤',
      planStatus: '步骤状态：{{status}}',
      planStepProgress: '{{step}}进度',
      planEdit: '编辑计划',
      planEditSummary: '操作摘要',
      planEditModel: '模型',
      planModelAutomatic: '自动选择',
      planEditPrompt: '提示词',
      planEditParameters: '参数（JSON）',
      planEditOutputCount: '输出数量',
      planEditReferences: '目标与参考素材',
      planEditReferenceRole: '参考素材角色',
      planEditRemoveReference: '移除参考素材',
      planEditAddReference: '添加参考素材',
      planEditSelectReference: '选择素材…',
      planEditAdd: '添加',
      planEditCancel: '取消',
      planEditSave: '保存并重新报价',
      planEditSaving: '正在重新报价…',
      planEditInvalidParameters: '请使用 JSON 对象格式填写参数。',
      planEditFailed: '计划更新失败，请检查字段后重试。',
      planAssumptions: 'Agent 已考虑的事项',
      planDetails: '查看详情',
      planFailureRealPerson:
        '该模型无法处理可能包含真人的图片。请改用插画或明确为虚拟角色的图片后重试。',
      planFailureGeneric: '生成失败。请检查输入素材或设置后重试。',
      approvePlanStepPrefix: '确认 ·',
      approvingPlanStep: '正在确认…',
      applyTimelineEdit: '应用修改',
      timelineEditPreview: '{{count}} 项时间线修改',
      timelineEditTarget: '{{name}} · 版本 {{revision}}',
      planPricingUnavailable: '暂时无法报价，执行已禁用',
      retryPlanStep: '重试',
      retryFailedOutputs: '重试失败项（{{count}}）',
      skipPlanStep: '跳过',
      saveWorkspaceReference: '保存为工作区引用',
      planInputs: '原始素材',
      planInputRole: {
        target: '目标',
        reference: '参考',
        identity_reference: '人物身份参考',
        motion_reference: '运镜与动作参考',
        first_frame: '首帧',
        source: '来源',
      },
      planConstraint: { keep: '保持', vary: '变化' },
      planExecutionStatus: {
        awaiting_confirmation: '等待用户确认…',
        queued: '排队中，尚未开始生成',
        processing: '正在生成媒体…',
        succeeded: '生成完成',
        failed: '生成失败，你仍可继续与智能助手对话。',
        cancelled: '生成已取消，你仍可继续与智能助手对话。',
      },
      planResultComplete: '已完成：{{summary}}',
      planResultCompleteWithOutputs:
        '已完成：{{summary}} 共生成 {{count}} 个结果，可以继续对这些结果提出修改要求。',
      planResultFailed:
        '计划执行未完成，{{count}} 个步骤失败。你可以展开计划查看详情并重试。',
    },
    videoParams: {
      eyebrow: '新视频输出',
      description: '根据已上传的图片和视频内容生成新的广告视频。',
      duration: '时长（秒）',
      durationHint: '请输入 4 到 30 之间的整数秒。',
      durationError: '时长必须在 4 到 30 秒之间。',
      aspectRatio: '画面比例',
      resolution: '分辨率',
      modelSelection: '模型选择',
      auto: '自动推荐',
      manual: '手动选择模型',
      model: '视频模型',
      paid: '付费套餐',
      badges: {
        economy: '经济',
        fast: '快速',
        professional: '专业',
        latest: '最新',
      },
      dynamicDurationHint: '请输入 4 到 {{max}} 之间的整数秒。',
      audio: '生成原生音频',
      audioHint: '模型支持时生成对白、环境声和音效。',
      adjusted: '输出规格已调整：{{before}} → {{after}}',
      calculating: '正在计算肥皂币…',
      estimatedPrefix: '预计肥皂币成本：',
      estimated: '预计积分成本：{{count}}',
      adjust: '调整设置以更新报价。',
      quoteErrorHint: '修改任一视频设置后可重新计算积分。',
      insufficientCredits:
        '视频生成未开始：当前可用积分不足，本次需要 {{count}} 积分。已保留当前设置，请补充积分或调整视频参数后重试。',
      generationFailed:
        '视频生成未开始：{{message}} 已保留当前设置，可以直接重试。',
      requestMessage:
        '生成视频：“{{direction}}” · {{duration}} 秒 · {{ratio}} · {{resolution}}',
      generate: '生成视频',
      generatePrefix: '生成 ·',
      generateWithCredits: '生成 · {{count}} 积分',
    },
    assetConfirmation: {
      accept: '接受',
      acceptHint: '将这条判断作为后续生成的参考',
      ignore: '忽略',
      ignoreHint: '不使用这条低置信度判断',
      closeTool: '关闭素材确认',
      previousQuestion: '上一个问题',
      nextQuestion: '下一个问题',
      stepOf: '/',
      skip: '跳过',
      required: '必填',
      requiredHint: '完成所有问题后即可继续生成。',
      submit: '确认选择并继续',
    },
    directVideoSetup: {
      ratioEyebrow: '视频画幅',
      invalidRatio: '暂不支持 {{ratio}}，请选择可用比例',
      chooseRatio: '请选择视频画面比例',
      ratioSelected: '已选择 {{ratio}}。',
      ratioSelectedChooseDirection: '已选择 {{ratio}}。接下来请选择视频方向。',
      direction: '视频方向',
      directionQuestion: '希望图片如何动起来？',
      directionPlaceholder: '描述动作、镜头和节奏',
      subtle: '自然微动',
      subtleHint: '保持原构图，加入细微自然动作',
      subtleValue: '保持原构图和主体一致，仅加入细微自然动作',
      pushIn: '电影感推进',
      pushInHint: '镜头缓慢推近，突出主体',
      pushInValue: '镜头缓慢向主体推进，营造电影感和空间层次',
      orbit: '环绕展示',
      orbitHint: '围绕主体平滑移动，展示细节',
      orbitValue: '镜头平滑环绕主体移动，稳定展示主体细节',
      dynamic: '动态揭晓',
      dynamicHint: '更明显的动作和节奏变化',
      dynamicValue: '以动态镜头和清晰节奏逐步揭晓主体',
      typeOwnAnswer: '输入自己的方向',
      customLabel: '{{field}}自定义内容',
      closeTool: '关闭方向选择',
      previousQuestion: '上一个问题',
      nextQuestion: '下一个问题',
      stepOf: '/',
      skip: '跳过',
      required: '必填',
      continue: '继续',
      submit: '使用此方向',
    },
  },
} as const satisfies TranslationResources;
